import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  getAdminCommissionStructures,
  updateUserCommissionAmount,
  setUserCommissionStructure,
  createAdminCommissionStructure,
  updateAdminCommissionStructure,
} from "../../../slices/admin/thunk";
import { formatCentsToDollars } from "../../../utils/utils";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const UserSpecificCommissionModal = ({ isOpen, setIsOpen, userProfile, currentUser, onRefresh }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [commissionStructures, setCommissionStructures] = useState([]);
  const [activeTab, setActiveTab] = useState("1");

  // Admin access validation
  const isCurrentUserAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  // Form states
  const [customAmount, setCustomAmount] = useState("");
  const [selectedStructureId, setSelectedStructureId] = useState("");
  const [amountError, setAmountError] = useState("");
  const [structureError, setStructureError] = useState("");

  // Breakdown editing states
  const [selectedStructureForEdit, setSelectedStructureForEdit] = useState("");
  const [isEditingBreakdown, setIsEditingBreakdown] = useState(false);
  const [editMode, setEditMode] = useState(""); // "edit" or "create"
  const [editBreakdown, setEditBreakdown] = useState({
    national: "",
    ga: "",
    broker: ""
  });
  const [breakdownErrors, setBreakdownErrors] = useState({});
  const [totalMismatchWarning, setTotalMismatchWarning] = useState("");
  const [structureName, setStructureName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const resetModal = () => {
    setCustomAmount(userProfile?.customCommissionTotalAmount || "1000");
    setSelectedStructureId(userProfile?.customCommissionStructureId || "");
    setSelectedStructureForEdit("");
    setAmountError("");
    setStructureError("");
    setIsEditingBreakdown(false);
    setEditMode("");
    setEditBreakdown({ national: "", ga: "", broker: "" });
    setBreakdownErrors({});
    setTotalMismatchWarning("");
    setStructureName("");
    setIsEditingName(false);
    setActiveTab("1");
  };

  const handleClose = () => {
    resetModal();
    setIsOpen(false);
  };

  const fetchCommissionStructures = async () => {
    try {
      // Get the national ID for this user - either they are national, or we need to find their national
      let nationalId = null;
      if (userProfile.role === DASHBOARD_USER_ROLES.NATIONAL) {
        nationalId = userProfile.id;
      } else {
        // For other roles, we would need to find their national ID through hierarchy
        // This might require additional API calls or data structure changes
        nationalId = userProfile.nationalId || userProfile.nationalInfo?.id;
      }

      const queryParams = nationalId ? { userId: nationalId } : {};
      const response = await dispatch(getAdminCommissionStructures(queryParams));
      if (response.payload?.commissionStructures) {
        setCommissionStructures(response.payload.commissionStructures);
      }
    } catch (error) {
      console.error("Error fetching commission structures:", error);
    }
  };

  useEffect(() => {
    if (isOpen && userProfile) {
      fetchCommissionStructures();
      resetModal();
    }
  }, [isOpen, userProfile]);

  // Get user display name
  const userName = userProfile ? (
    userProfile.role === DASHBOARD_USER_ROLES.BROKER
      ? `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim()
      : userProfile.companyName || `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim()
  ) : "";

  const validateAmountForm = () => {
    let isValid = true;
    if (!customAmount || parseFloat(customAmount) < 0) {
      setAmountError("Amount must be a positive number");
      isValid = false;
    } else {
      setAmountError("");

      // Check if custom amount is less than current breakdown total
      const currentStructure = getCurrentStructureDetails();
      const currentTotal = currentStructure.national + currentStructure.ga + currentStructure.broker;
      const newAmount = parseFloat(customAmount);

      if (newAmount < currentTotal) {
        setTotalMismatchWarning(
          `Warning: Custom amount ($${formatCentsToDollars(newAmount)}) is less than current breakdown total ($${formatCentsToDollars(currentTotal)}). Consider updating the breakdown or the amount.`
        );
      } else {
        setTotalMismatchWarning("");
      }
    }
    return isValid;
  };

  const validateStructureForm = () => {
    let isValid = true;
    if (!selectedStructureId) {
      setStructureError("Please select a commission structure");
      isValid = false;
    } else {
      setStructureError("");
    }
    return isValid;
  };

  const validateBreakdownForm = () => {
    const errors = {};
    let isValid = true;

    const national = parseFloat(editBreakdown.national) || 0;
    const ga = parseFloat(editBreakdown.ga) || 0;
    const broker = parseFloat(editBreakdown.broker) || 0;

    if (national < 0) {
      errors.national = "National amount must be >= 0";
      isValid = false;
    }
    if (ga < 0) {
      errors.ga = "GA amount must be >= 0";
      isValid = false;
    }
    if (broker < 0) {
      errors.broker = "Broker amount must be >= 0";
      isValid = false;
    }

    // Check if name is provided
    if (!structureName.trim()) {
      errors.name = "Structure name is required";
      isValid = false;
    }

    // Check if total matches custom amount
    const total = national + ga + broker;
    const targetAmount = parseFloat(customAmount) || 1000;

    if (total !== targetAmount) {
      errors.total = `Breakdown total (${formatCentsToDollars(total)}) must equal custom amount (${formatCentsToDollars(targetAmount)})`;
      isValid = false;
    }

    setBreakdownErrors(errors);
    return isValid;
  };

  const handleStartEditMode = (mode) => {
    setEditMode(mode);
    setIsEditingBreakdown(true);
    setBreakdownErrors({});
    setIsEditingName(false);

    if (mode === "edit") {
      // Editing the selected structure
      const structure = getSelectedStructureForEditDetails();
      setEditBreakdown({
        national: structure.national.toString(),
        ga: structure.ga.toString(),
        broker: structure.broker.toString()
      });
      setStructureName(structure.name || "");
    } else if (mode === "create") {
      // Creating new structure - start with empty/default values
      setEditBreakdown({
        national: "300",
        ga: "300",
        broker: "400"
      });
      setStructureName(`Custom for ${userName}`);
      setIsEditingName(true); // Name is editable by default when creating
    }
  };

  const handleCancelEditBreakdown = () => {
    setIsEditingBreakdown(false);
    setEditMode("");
    setEditBreakdown({ national: "", ga: "", broker: "" });
    setBreakdownErrors({});
    setStructureName("");
    setIsEditingName(false);
  };

  const handleSaveBreakdown = async () => {
    if (!validateBreakdownForm()) return;

    try {
      setLoading(true);

      if (editMode === "edit") {
        // Update the selected structure
        const structureData = {
          name: structureName.trim(),
          national: parseFloat(editBreakdown.national),
          ga: parseFloat(editBreakdown.ga),
          broker: parseFloat(editBreakdown.broker),
        };

        await dispatch(updateAdminCommissionStructure({
          structureId: selectedStructureForEdit,
          structureData,
        }));

        Swal.fire({
          title: "Success!",
          text: "Commission structure updated successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } else if (editMode === "create") {
        // Create a new custom structure
        const structureData = {
          name: structureName.trim(),
          national: parseFloat(editBreakdown.national),
          ga: parseFloat(editBreakdown.ga),
          broker: parseFloat(editBreakdown.broker),
          nationalId: userProfile.role === DASHBOARD_USER_ROLES.NATIONAL ? userProfile.id : (userProfile.nationalId || userProfile.nationalInfo?.id),
          dashboardUserId: userProfile.id,
          isDefault: false,
        };

        // Create new structure
        const createResponse = await dispatch(createAdminCommissionStructure(structureData));

        if (createResponse.payload?.commissionStructure) {
          Swal.fire({
            title: "Success!",
            text: "New commission structure created successfully",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      }

      setIsEditingBreakdown(false);
      setEditMode("");
      setEditBreakdown({ national: "", ga: "", broker: "" });
      setBreakdownErrors({});
      setStructureName("");
      setIsEditingName(false);

      // Refresh data
      await fetchCommissionStructures();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error saving breakdown:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to save commission breakdown",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAmount = async () => {
    if (!validateAmountForm()) return;

    try {
      setLoading(true);
      await dispatch(updateUserCommissionAmount({
        userId: userProfile.id,
        customCommissionTotalAmount: parseFloat(customAmount),
      }));

      Swal.fire({
        title: "Success!",
        text: "Commission amount updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating commission amount:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update commission amount",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStructure = async () => {
    if (!validateStructureForm()) return;

    try {
      setLoading(true);
      await dispatch(setUserCommissionStructure({
        userId: userProfile.id,
        customCommissionStructureId: selectedStructureId === "default" ? null : selectedStructureId,
      }));

      Swal.fire({
        title: "Success!",
        text: "Commission structure updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating commission structure:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update commission structure",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return <Badge color="primary" pill>National</Badge>;
      case DASHBOARD_USER_ROLES.GA:
        return <Badge color="info" pill>GA</Badge>;
      case DASHBOARD_USER_ROLES.BROKER:
        return <Badge color="success" pill>Broker</Badge>;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return <Badge color="warning" pill>Employer</Badge>;
      case DASHBOARD_USER_ROLES.ADMIN:
        return <Badge color="danger" pill>Admin</Badge>;
      default:
        return <Badge color="secondary" pill>{role}</Badge>;
    }
  };

  const getCurrentStructureName = () => {
    if (!userProfile?.customCommissionStructureId) {
      return "Default Structure";
    }
    const structure = commissionStructures.find(s => s.id === userProfile.customCommissionStructureId);
    return structure ? structure.name : "Unknown Structure";
  };

  const getCurrentStructureDetails = () => {
    if (!userProfile?.customCommissionStructureId) {
      return { national: 300, ga: 300, broker: 400 }; // Default structure
    }
    const structure = commissionStructures.find(s => s.id === userProfile.customCommissionStructureId);
    return structure || { national: 0, ga: 0, broker: 0 };
  };

  const getPreviewStructureDetails = () => {
    if (selectedStructureId === "" || selectedStructureId === "default") {
      return { national: 300, ga: 300, broker: 400 }; // Default structure
    }
    const structure = commissionStructures.find(s => s.id === selectedStructureId);
    return structure || { national: 0, ga: 0, broker: 0 };
  };

  const getSelectedStructureForEditDetails = () => {
    if (selectedStructureForEdit === "" || selectedStructureForEdit === "default") {
      return { name: "Default Structure", national: 300, ga: 300, broker: 400, id: "default" };
    }
    const structure = commissionStructures.find(s => s.id === selectedStructureForEdit);
    return structure || { name: "", national: 0, ga: 0, broker: 0, id: "" };
  };

  // Security check: Only allow admin users
  if (!isCurrentUserAdmin) {
    return null;
  }

  if (!userProfile) {
    return null;
  }

  const currentStructure = getCurrentStructureDetails();

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Commission Settings for {userName}
      </ModalHeader>
      <ModalBody>
        {/* Tabs Navigation */}
        <Nav tabs>
          <NavItem>
            <NavLink
              className={activeTab === "1" ? "active" : ""}
              onClick={() => toggleTab("1")}
              style={{
                cursor: "pointer",
                color: activeTab === "1" ? "#000" : "#6c757d"
              }}
            >
              General Info
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === "2" ? "active" : ""}
              onClick={() => toggleTab("2")}
              style={{
                cursor: "pointer",
                color: activeTab === "2" ? "#000" : "#6c757d"
              }}
            >
              Manage Structures
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="mt-3">
          {/* TAB 1: General Info */}
          <TabPane tabId="1">
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">User Information</h6>
                  </CardHeader>
                  <CardBody>
                    <p><strong>Name:</strong> {userName}</p>
                    <p><strong>Email:</strong> {userProfile.email}</p>
                    <p><strong>Role:</strong> {getRoleIcon(userProfile.role)}</p>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Commission Total Amount</h6>
                  </CardHeader>
                  <CardBody>
                    <div className="h4 text-primary mb-2">
                      ${formatCentsToDollars(userProfile.customCommissionTotalAmount || 1000)}
                    </div>
                    <small className="text-muted">Total commission to distribute across roles</small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <h6 className="mb-0 d-flex align-items-center">
                      Currently Selected Commission Structure
                      <Badge color="success" className="ms-2">Active</Badge>
                    </h6>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <strong>Structure Name:</strong> {getCurrentStructureName()}
                    </div>
                    <Row>
                      <Col md={4}>
                        <div className="text-muted small">National</div>
                        <div className="h5 mb-0">${formatCentsToDollars(currentStructure.national)}</div>
                      </Col>
                      <Col md={4}>
                        <div className="text-muted small">GA</div>
                        <div className="h5 mb-0">${formatCentsToDollars(currentStructure.ga)}</div>
                      </Col>
                      <Col md={4}>
                        <div className="text-muted small">Broker</div>
                        <div className="h5 mb-0">${formatCentsToDollars(currentStructure.broker)}</div>
                      </Col>
                    </Row>
                    <hr />
                    <div className="text-end">
                      <strong>Total: </strong>
                      ${formatCentsToDollars(currentStructure.national + currentStructure.ga + currentStructure.broker)}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* TAB 2: Manage Structures */}
          <TabPane tabId="2">
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Custom Total Commission Amount</h6>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <FormGroup>
                        <Label>Total Amount (cents)</Label>
                        <Input
                          type="number"
                          value={customAmount}
                          onChange={(e) => {
                            setCustomAmount(e.target.value);
                            if (amountError) setAmountError("");
                            setTotalMismatchWarning("");
                          }}
                          invalid={!!amountError}
                          placeholder="1000"
                          min="0"
                        />
                        {amountError && <div className="invalid-feedback">{amountError}</div>}
                        <small className="text-muted">
                          Current: ${formatCentsToDollars(userProfile.customCommissionTotalAmount || 1000)}
                          <br />
                          Default is 1000 cents ($10.00)
                        </small>
                      </FormGroup>

                      {totalMismatchWarning && (
                        <Alert color="warning" className="py-2 mb-3">
                          <i className="ri-alert-line me-1"></i>
                          {totalMismatchWarning}
                        </Alert>
                      )}
                      <Button
                        color="primary"
                        onClick={handleUpdateAmount}
                        disabled={loading}
                        block
                      >
                        {loading ? <Spinner size="sm" className="me-1" /> : null}
                        Update Amount
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Select Commission Structure</h6>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <FormGroup>
                        <Label>Commission Structure</Label>
                        <Input
                          type="select"
                          value={selectedStructureId}
                          onChange={(e) => {
                            setSelectedStructureId(e.target.value);
                            if (structureError) setStructureError("");
                          }}
                          invalid={!!structureError}
                        >
                          <option value="">Select structure...</option>
                          <option value="default">Default Structure</option>
                          {commissionStructures.map((structure) => (
                            <option key={structure.id} value={structure.id}>
                              {structure.name}
                            </option>
                          ))}
                        </Input>
                        {structureError && <div className="invalid-feedback">{structureError}</div>}
                        <small className="text-muted">
                          Current: {getCurrentStructureName()}
                        </small>
                      </FormGroup>

                      {selectedStructureId && selectedStructureId !== userProfile.customCommissionStructureId && (
                        <div className="mb-3">
                          <Alert color="info" className="py-2">
                            <strong>Preview New Structure:</strong>
                            {(() => {
                              const structure = getPreviewStructureDetails();
                              return (
                                <div className="mt-1">
                                  National: ${formatCentsToDollars(structure.national)} |
                                  GA: ${formatCentsToDollars(structure.ga)} |
                                  Broker: ${formatCentsToDollars(structure.broker)}
                                </div>
                              );
                            })()}
                          </Alert>
                        </div>
                      )}

                      <Button
                        color="primary"
                        onClick={handleUpdateStructure}
                        disabled={loading}
                        block
                      >
                        {loading ? <Spinner size="sm" className="me-1" /> : null}
                        Assign Structure to User
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Create New Structure Section */}
            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Create New Commission Structure</h6>
                      {!isEditingBreakdown && (
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleStartEditMode("create")}
                          disabled={loading}
                        >
                          <i className="ri-add-line me-1"></i>
                          Create New
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody>
                    {isEditingBreakdown && editMode === "create" ? (
                      <div>
                        <Alert color="info" className="py-2 mb-3">
                          <i className="ri-information-line me-1"></i>
                          Creating a brand new commission structure
                        </Alert>

                        {/* Structure Name */}
                        <div className="mb-3">
                          <FormGroup>
                            <Label><strong>Structure Name *</strong></Label>
                            <Input
                              type="text"
                              value={structureName}
                              onChange={(e) => {
                                setStructureName(e.target.value);
                                if (breakdownErrors.name) {
                                  setBreakdownErrors(prev => ({ ...prev, name: "" }));
                                }
                              }}
                              invalid={!!breakdownErrors.name}
                              placeholder="e.g., Custom High Broker Commission"
                            />
                            {breakdownErrors.name && (
                              <div className="invalid-feedback">{breakdownErrors.name}</div>
                            )}
                          </FormGroup>
                        </div>

                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>National (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.national}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, national: e.target.value }));
                                  if (breakdownErrors.national) {
                                    setBreakdownErrors(prev => ({ ...prev, national: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.national}
                                min="0"
                              />
                              {breakdownErrors.national && (
                                <div className="invalid-feedback">{breakdownErrors.national}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.national || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>GA (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.ga}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, ga: e.target.value }));
                                  if (breakdownErrors.ga) {
                                    setBreakdownErrors(prev => ({ ...prev, ga: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.ga}
                                min="0"
                              />
                              {breakdownErrors.ga && (
                                <div className="invalid-feedback">{breakdownErrors.ga}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.ga || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Broker (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.broker}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, broker: e.target.value }));
                                  if (breakdownErrors.broker) {
                                    setBreakdownErrors(prev => ({ ...prev, broker: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.broker}
                                min="0"
                              />
                              {breakdownErrors.broker && (
                                <div className="invalid-feedback">{breakdownErrors.broker}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.broker || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        {breakdownErrors.total && (
                          <Alert color="danger" className="py-2 mb-2">
                            {breakdownErrors.total}
                          </Alert>
                        )}

                        <div className="text-end mb-3">
                          <strong>Total: </strong>
                          ${formatCentsToDollars(
                            (parseInt(editBreakdown.national) || 0) +
                            (parseInt(editBreakdown.ga) || 0) +
                            (parseInt(editBreakdown.broker) || 0)
                          )}
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={handleCancelEditBreakdown}
                            disabled={loading}
                          >
                            Cancel
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleSaveBreakdown}
                            disabled={loading}
                          >
                            {loading ? <Spinner size="sm" className="me-1" /> : null}
                            Create Structure
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted mb-0">
                        Click "Create New" to create a brand new commission structure
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Edit Existing Structure Section */}
            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <h6 className="mb-0">Edit Existing Commission Structure</h6>
                  </CardHeader>
                  <CardBody>
                    {/* Step 1: Select structure to edit */}
                    <FormGroup className="mb-4">
                      <Label><strong>Select Structure to Edit</strong></Label>
                      <Input
                        type="select"
                        value={selectedStructureForEdit}
                        onChange={(e) => {
                          setSelectedStructureForEdit(e.target.value);
                          setIsEditingBreakdown(false);
                          setEditMode("");
                        }}
                        disabled={isEditingBreakdown}
                      >
                        <option value="">Choose a structure...</option>
                        <option value="default">Default Structure</option>
                        {commissionStructures.map((structure) => (
                          <option key={structure.id} value={structure.id}>
                            {structure.name}
                          </option>
                        ))}
                      </Input>
                      <small className="text-muted">
                        Select which commission structure you want to view or edit
                      </small>
                    </FormGroup>

                    {/* Step 2: Show breakdown of selected structure */}
                    {selectedStructureForEdit && !isEditingBreakdown && (
                      <div>
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Structure Breakdown</h6>
                          <Row>
                            <Col md={4}>
                              <div className="text-muted small">National</div>
                              <div className="h5 mb-0">
                                ${formatCentsToDollars(getSelectedStructureForEditDetails().national)}
                              </div>
                            </Col>
                            <Col md={4}>
                              <div className="text-muted small">GA</div>
                              <div className="h5 mb-0">
                                ${formatCentsToDollars(getSelectedStructureForEditDetails().ga)}
                              </div>
                            </Col>
                            <Col md={4}>
                              <div className="text-muted small">Broker</div>
                              <div className="h5 mb-0">
                                ${formatCentsToDollars(getSelectedStructureForEditDetails().broker)}
                              </div>
                            </Col>
                          </Row>
                          <hr />
                          <div className="text-end">
                            <strong>Total: </strong>
                            ${formatCentsToDollars(
                              getSelectedStructureForEditDetails().national +
                              getSelectedStructureForEditDetails().ga +
                              getSelectedStructureForEditDetails().broker
                            )}
                          </div>
                        </div>

                        {/* Step 3: Action button */}
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleStartEditMode("edit")}
                            disabled={loading || selectedStructureForEdit === "default"}
                          >
                            <i className="ri-pencil-line me-1"></i>
                            Edit Structure
                          </Button>
                        </div>
                        {selectedStructureForEdit === "default" && (
                          <small className="text-muted d-block mt-2 text-end">
                            Note: Default structure cannot be edited
                          </small>
                        )}
                      </div>
                    )}

                    {selectedStructureForEdit && !isEditingBreakdown && !selectedStructureForEdit && (
                      <p className="text-muted mb-0">
                        Select a structure from the dropdown to view and edit it
                      </p>
                    )}

                    {/* Step 4: Edit form */}
                    {isEditingBreakdown && editMode === "edit" && (
                      <div>
                        <Alert color="info" className="py-2 mb-3">
                          <i className="ri-information-line me-1"></i>
                          {editMode === "edit"
                            ? "Editing structure: This will update the structure for all users assigned to it"
                            : "Creating new structure: This will create a brand new commission structure"
                          }
                        </Alert>

                        {/* Structure Name */}
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <Label className="mb-0"><strong>Structure Name</strong></Label>
                            {!isEditingName && editMode === "edit" && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setIsEditingName(true)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                            )}
                          </div>
                          {isEditingName || editMode === "create" ? (
                            <Input
                              type="text"
                              value={structureName}
                              onChange={(e) => {
                                setStructureName(e.target.value);
                                if (breakdownErrors.name) {
                                  setBreakdownErrors(prev => ({ ...prev, name: "" }));
                                }
                              }}
                              invalid={!!breakdownErrors.name}
                              placeholder="e.g., Custom High Broker Commission"
                            />
                          ) : (
                            <div className="form-control-plaintext fw-semibold">
                              {structureName || "N/A"}
                            </div>
                          )}
                          {breakdownErrors.name && (
                            <div className="text-danger small mt-1">{breakdownErrors.name}</div>
                          )}
                        </div>

                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>National (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.national}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, national: e.target.value }));
                                  if (breakdownErrors.national) {
                                    setBreakdownErrors(prev => ({ ...prev, national: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.national}
                                min="0"
                              />
                              {breakdownErrors.national && (
                                <div className="invalid-feedback">{breakdownErrors.national}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.national || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>GA (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.ga}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, ga: e.target.value }));
                                  if (breakdownErrors.ga) {
                                    setBreakdownErrors(prev => ({ ...prev, ga: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.ga}
                                min="0"
                              />
                              {breakdownErrors.ga && (
                                <div className="invalid-feedback">{breakdownErrors.ga}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.ga || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Broker (cents)</Label>
                              <Input
                                type="number"
                                value={editBreakdown.broker}
                                onChange={(e) => {
                                  setEditBreakdown(prev => ({ ...prev, broker: e.target.value }));
                                  if (breakdownErrors.broker) {
                                    setBreakdownErrors(prev => ({ ...prev, broker: "" }));
                                  }
                                }}
                                invalid={!!breakdownErrors.broker}
                                min="0"
                              />
                              {breakdownErrors.broker && (
                                <div className="invalid-feedback">{breakdownErrors.broker}</div>
                              )}
                              <small className="text-muted">
                                ${formatCentsToDollars(editBreakdown.broker || 0)}
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        {breakdownErrors.total && (
                          <Alert color="danger" className="py-2 mb-2">
                            {breakdownErrors.total}
                          </Alert>
                        )}

                        <div className="text-end mb-3">
                          <strong>Total: </strong>
                          ${formatCentsToDollars(
                            (parseInt(editBreakdown.national) || 0) +
                            (parseInt(editBreakdown.ga) || 0) +
                            (parseInt(editBreakdown.broker) || 0)
                          )}
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={handleCancelEditBreakdown}
                            disabled={loading}
                          >
                            Cancel
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleSaveBreakdown}
                            disabled={loading}
                          >
                            {loading ? <Spinner size="sm" className="me-1" /> : null}
                            {editMode === "edit" ? "Update Structure" : "Create Structure"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserSpecificCommissionModal;
